import React, { useState, useEffect } from 'react';
import { deliveriesAPI, pickupEventsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

function DriverDashboard() {
  const [deliveries, setDeliveries] = useState([]);
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [upcomingPickups, setUpcomingPickups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('assigned');
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDeliveries();
    loadPendingDeliveries();
    loadUpcomingPickups();
  }, []);

  const loadDeliveries = async () => {
    try {
      const response = await deliveriesAPI.getMyDeliveries();
      setDeliveries(response.data);
    } catch (error) {
      console.error('Error loading deliveries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingDeliveries = async () => {
    try {
      const response = await deliveriesAPI.getPending();
      setPendingDeliveries(response.data);
    } catch (error) {
      console.error('Error loading pending deliveries:', error);
    }
  };

  const loadUpcomingPickups = async () => {
    try {
      const response = await pickupEventsAPI.getUpcoming();
      setUpcomingPickups(response.data);
    } catch (error) {
      console.error('Error loading upcoming pickups:', error);
    }
  };

  const handleAssign = async (deliveryId) => {
    try {
      await deliveriesAPI.assign(deliveryId);
      loadDeliveries();
      loadPendingDeliveries();
    } catch (error) {
      alert('Error assigning delivery');
    }
  };

  const handleStart = async (deliveryId) => {
    try {
      await deliveriesAPI.start(deliveryId);
      loadDeliveries();
    } catch (error) {
      alert('Error starting delivery');
    }
  };

  const handleComplete = async (deliveryId) => {
    const notes = prompt('Enter delivery notes (optional):');
    try {
      await deliveriesAPI.complete(deliveryId, notes);
      loadDeliveries();
    } catch (error) {
      alert('Error completing delivery');
    }
  };

  const handlePhotoUpload = async (deliveryId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingPhoto(deliveryId);
    const formData = new FormData();
    formData.append('photo', file);

    try {
      await deliveriesAPI.uploadPhoto(deliveryId, formData);
      alert('Photo uploaded successfully!');
      loadDeliveries();
    } catch (error) {
      alert('Error uploading photo: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setUploadingPhoto(null);
    }
  };

  const filteredDeliveries = deliveries.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page dashboard-page">
      <h1 className="page-title">Driver Dashboard</h1>

      {upcomingPickups.length > 0 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <h2>Upcoming Pickup Events</h2>
          <ul className="pickup-list">
            {upcomingPickups.map((pickup) => (
              <li key={pickup.id} className="pickup-item">
                <div className="pickup-info">
                  <strong>{new Date(pickup.scheduled_datetime).toLocaleString()}</strong>
                  <div className="pickup-details">
                    <span>{pickup.location}</span>
                    <span className="pickup-code">Code: {pickup.pickup_code}</span>
                  </div>
                  <div className="pickup-stats">
                    <span>{pickup.attendances?.length || 0} drivers attending</span>
                    <span>•</span>
                    <span>{pickup.deliveries?.length || 0} deliveries</span>
                    {pickup.attendances?.find(a => a.confirmed) && (
                      <span className="confirmed-badge">✓ You confirmed</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/admin/pickup-events/${pickup.id}`)}
                  className="btn btn-primary btn-sm"
                >
                  View Details
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card">
        <h2>Available Deliveries</h2>
        {pendingDeliveries.length === 0 ? (
          <p>No pending deliveries</p>
        ) : (
          <ul className="delivery-list">
            {pendingDeliveries.map((delivery) => (
              <li key={delivery.id} className="delivery-item">
                <div>
                  Delivery #{delivery.id} - Order #{delivery.order_id}
                </div>
                <button
                  onClick={() => handleAssign(delivery.id)}
                  className="btn btn-primary"
                >
                  Accept Delivery
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <div className="delivery-header">
          <h2>My Deliveries</h2>
          <div className="filter-buttons">
            <button
              className={filter === 'assigned' ? 'active' : ''}
              onClick={() => setFilter('assigned')}
            >
              Assigned
            </button>
            <button
              className={filter === 'in_transit' ? 'active' : ''}
              onClick={() => setFilter('in_transit')}
            >
              In Transit
            </button>
            <button
              className={filter === 'delivered' ? 'active' : ''}
              onClick={() => setFilter('delivered')}
            >
              Delivered
            </button>
            <button
              className={filter === 'all' ? 'active' : ''}
              onClick={() => setFilter('all')}
            >
              All
            </button>
          </div>
        </div>

        {filteredDeliveries.length === 0 ? (
          <p>No deliveries</p>
        ) : (
          <ul className="delivery-list">
            {filteredDeliveries.map((delivery) => (
              <li key={delivery.id} className="delivery-item-expanded">
                <div className="delivery-info">
                  <strong>Delivery #{delivery.id}</strong>
                  <br />
                  Order: {delivery.order?.order_number}
                  <br />
                  Customer: {delivery.customer?.name}
                  <br />
                  Address: {delivery.customer?.address}
                  <br />
                  <span className={`status-badge status-${delivery.status}`}>
                    {delivery.status}
                  </span>
                </div>
                <div className="delivery-actions">
                  {delivery.status === 'assigned' && (
                    <button
                      onClick={() => handleStart(delivery.id)}
                      className="btn btn-primary"
                    >
                      Start Delivery
                    </button>
                  )}
                  {delivery.status === 'in_transit' && (
                    <>
                      <button
                        onClick={() => handleComplete(delivery.id)}
                        className="btn btn-success"
                      >
                        Mark Complete
                      </button>
                      <label className="btn btn-secondary" style={{ marginLeft: '10px' }}>
                        {uploadingPhoto === delivery.id ? 'Uploading...' : 'Upload Photo'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(delivery.id, e)}
                          style={{ display: 'none' }}
                          disabled={uploadingPhoto === delivery.id}
                        />
                      </label>
                    </>
                  )}
                  {delivery.status === 'delivered' && delivery.photos && delivery.photos.length === 0 && (
                    <label className="btn btn-secondary">
                      {uploadingPhoto === delivery.id ? 'Uploading...' : 'Add Photo'}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(delivery.id, e)}
                        style={{ display: 'none' }}
                        disabled={uploadingPhoto === delivery.id}
                      />
                    </label>
                  )}
                </div>

                {delivery.photos && delivery.photos.length > 0 && (
                  <div className="delivery-photos" style={{ marginTop: '15px' }}>
                    <strong>Photos ({delivery.photos.length}):</strong>
                    <div className="photos-grid">
                      {delivery.photos.map((photo) => (
                        <div key={photo.id} className="photo-item">
                          <img src={photo.photo_url} alt={photo.caption || 'Delivery photo'} />
                          {photo.caption && <p className="photo-caption">{photo.caption}</p>}
                        </div>
                      ))}
                    </div>
                    {delivery.status !== 'delivered' && (
                      <label className="btn btn-secondary btn-sm" style={{ marginTop: '10px' }}>
                        {uploadingPhoto === delivery.id ? 'Uploading...' : 'Add Another Photo'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePhotoUpload(delivery.id, e)}
                          style={{ display: 'none' }}
                          disabled={uploadingPhoto === delivery.id}
                        />
                      </label>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default DriverDashboard;
